import { db } from './indexeddb'
import type { Subtask, Task } from './models'

/**
 * **Alte Unteraufgaben → Aufgaben mit Elternteil** (T14 #179, Etappe 2).
 *
 * Bis zu dieser Version lebten Unteraufgaben in der Tabelle `subtasks`. Jedes Gerät
 * wandelt SEINE selbst um, im Moment seines Updates — nicht per SQL in der Cloud: Das
 * träfe alle Konten zugleich, und eine App mit älterem Stand zeigte die Unteraufgaben
 * dann als Aufgaben (.claude/tasks/subtasks-as-tasks.md, Grill 11).
 *
 * - **Vorher eine Sicherung** als JSON-Datei ins App-Datenverzeichnis (Grill 12). Schlägt
 *   sie fehl, wird nicht umgewandelt — es ist ein Versuch beim nächsten Start.
 * - Die neue Aufgabe behält die **lokale Kennung** und übernimmt die **Cloud-Kennung**
 *   der alten Zeile (`adoptCloudId`): Ein zweites Gerät desselben Kontos trifft dieselbe
 *   Zeile, statt sie doppelt anzulegen.
 * - Ort und Lebensbereich kommen vom Elternteil; ohne Elternteil auf dem Gerät bleibt die
 *   Zeile liegen, bis es da ist. Unter einer gelöschten Aufgabe entsteht keine Aufgabe —
 *   in der Cloud käme sie sonst lebend an (der Create-Payload kennt kein `deleted_at`).
 * - Lebt eine schon umgewandelte Zeile wieder auf (ein Gerät mit älterem Stand hat sie
 *   geändert), gehen Titel und Haken an die Aufgabe.
 * - Die alte Zeile wird weich gelöscht, in der Cloud ebenso. Offene Warteschlangen-
 *   Einträge für sie fallen weg: Sie gingen sonst noch als `subtasks`-Zeile hoch.
 *
 * Läuft beim Start und nach jedem Holen der alten Tabelle (Geräte mit älterem Stand
 * schreiben dort weiter). Ohne alte Zeilen kostet es eine leere Abfrage.
 *
 * @returns wie viele Zeilen umgewandelt wurden
 */
export function convertLegacySubtasks(options: { silent?: boolean } = {}): Promise<number> {
  // Mehrere Aufrufer (Start, Sync) — aber nur ein Lauf zur Zeit.
  running ??= run(options.silent ?? false).finally(() => { running = null })
  return running
}

let running: Promise<number> | null = null

type BackupApi = { invoke?: (channel: string, ...args: unknown[]) => Promise<unknown> }

async function writeBackup(subtasks: Subtask[], parents: Task[]): Promise<void> {
  const api = (globalThis as { api?: BackupApi }).api
  // Ohne Hauptprozess (Web) gibt es kein App-Datenverzeichnis; dort läuft auch kein Dexie mit echten Daten.
  if (!api?.invoke) return
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  await api.invoke(
    'backup:write-file',
    `before-subtasks-as-tasks-${stamp}.json`,
    JSON.stringify({ exportedAt: new Date().toISOString(), reason: 'T14 #179: subtasks become tasks', tasks: parents, subtasks }),
  )
}

/** `silent`: aus dem Sync heraus, der die Warteschlange gleich selbst liest (wie `queueSyncChange`). */
async function run(silent: boolean): Promise<number> {
  const legacy = await db.subtasks.filter(s => !s.deletedAt).toArray()
  if (legacy.length === 0) return 0

  const parentIds = [...new Set(legacy.map(s => s.taskId))]
  const parents = new Map(
    (await db.tasks.bulkGet(parentIds)).filter((t): t is Task => t != null).map(t => [t.id, t]),
  )
  const ready = legacy.filter(s => parents.has(s.taskId))
  if (ready.length === 0) return 0

  try {
    await writeBackup(legacy, [...parents.values()])
  } catch (error) {
    console.error('[Subtasks] Sicherung fehlgeschlagen — keine Umwandlung', error)
    return 0
  }

  let converted = 0
  await db.transaction('rw', db.tasks, db.subtasks, db.syncQueue, async () => {
    for (const sub of ready) {
      const parent = parents.get(sub.taskId)!
      const existing = await db.tasks.get(sub.id)
      if (existing) {
        // Schon umgewandelt, aber die alte Zeile lebt wieder: Ein Gerät mit älterem Stand
        // hat sie geändert. Seine Änderung geht an die Aufgabe, statt verloren zu gehen.
        const status = sub.done ? 'completed' : 'todo'
        if (existing.title !== sub.title || existing.status !== status) {
          await db.tasks.update(sub.id, { title: sub.title, status, completedAt: sub.done ? (existing.completedAt ?? sub.updatedAt) : null })
          await db.queueSyncChange('update', 'tasks', { localId: sub.id }, { silent })
        }
      } else if (!parent.deletedAt) {
        const row: Task = {
          id: sub.id,
          title: sub.title,
          description: null,
          status: sub.done ? 'completed' : 'todo',
          attention: null,
          category: 'other',
          lifeSpace: parent.lifeSpace,
          projectId: parent.projectId,
          sectionId: parent.sectionId,
          userId: sub.userId,
          cloudId: null,
          adoptCloudId: sub.cloudId,
          startDate: null,
          startTime: null,
          startPeriod: null,
          dueDate: null,
          dueTime: null,
          duePeriod: null,
          completedAt: sub.done ? sub.updatedAt : null,
          estimatedDuration: null,
          actualDuration: null,
          durationMode: null,
          tags: [],
          parentTaskId: parent.id,
          order: sub.sortOrder,
          dayOrder: null,
          deletedAt: null,
          pausedAt: null,
          rewardId: null,
          recurrence: null,
          importedFrom: sub.importedFrom ?? null,
          createdAt: sub.createdAt,
          updatedAt: sub.updatedAt,
          lastModified: sub.lastModified,
          synced: false,
          syncedAt: null,
        }
        await db.tasks.add(row)
        await db.queueSyncChange('create', 'tasks', { localId: row.id }, { silent })
        converted++
      }

      const pending = await db.syncQueue.filter(q => q.table === 'subtasks' && q.data?.localId === sub.id).primaryKeys()
      await db.syncQueue.bulkDelete(pending)
      await db.subtasks.update(sub.id, { deletedAt: new Date() })
      if (sub.cloudId) await db.queueSyncChange('delete', 'subtasks', { localId: sub.id, cloudId: sub.cloudId }, { silent })
    }
  })
  return converted
}
